import React, { useState } from 'react';
import { CreditCard, Calendar, Wallet, ShieldAlert, FileText, ArrowRight, CheckCircle2, Bot } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatSAR } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * ResolutionOptions — side-by-side comparator of repayment strategies.
 * Shows expected debtor outcome, our recovery %, time-to-resolve, and an action button.
 */
export const ResolutionOptions = ({ debtor: d, onPick }) => {
  const [picked, setPicked] = useState(null);
  if (!d) return null;

  const options = [
    {
      id: 'pay_now',
      title: 'Pay in full now',
      icon: Wallet,
      headline: formatSAR(d.amount),
      detail: 'Single SADAD bill / payment link, instant closure',
      recovery: 100,
      time: 'Same day',
      pros: ['Highest recovery', 'No follow-up needed', 'Case closes today'],
      cons: ['Lowest take-rate', 'Often requires liquidity'],
      tone: 'success',
    },
    {
      id: 'ptp',
      title: 'Promise to pay',
      icon: Calendar,
      headline: 'Date + amount',
      detail: 'Verbal/written commitment, monitored against SLA',
      recovery: 70,
      time: '3–14 days',
      pros: ['Lightweight', 'Tests intent', 'No paperwork'],
      cons: ['Higher break risk', 'Less binding'],
      tone: 'primary',
    },
    {
      id: 'plan',
      title: 'Installment plan',
      icon: CreditCard,
      headline: `6 × ${formatSAR(Math.round(d.amount / 6))}`,
      detail: '6-month interest-free plan, auto-debit via SADAD',
      recovery: 88,
      time: '6 months',
      pros: ['Predictable cashflow', 'Best for mid-value', 'Highest expected return'],
      cons: ['Longer monitoring', 'Reconciliation overhead'],
      tone: 'primary',
      recommended: d.amount > 5000 && d.amount < 80000,
    },
    {
      id: 'settle',
      title: 'Settlement offer',
      icon: FileText,
      headline: formatSAR(Math.round(d.amount * 0.75)),
      detail: '25% discount in exchange for immediate single payment',
      recovery: 75,
      time: 'Same week',
      pros: ['Clears stale balance', 'Frees portfolio capacity'],
      cons: ['Requires supervisor approval', 'Margin impact'],
      tone: 'warning',
      gateReason: 'Requires supervisor approval (>10% discount)',
    },
    {
      id: 'hardship',
      title: 'Hardship plan',
      icon: ShieldAlert,
      headline: `12 × ${formatSAR(Math.round(d.amount / 12))}`,
      detail: 'Reduced-payment plan with income/expense intake',
      recovery: 65,
      time: '12 months',
      pros: ['Protects vulnerable customers', 'PDPL-aligned'],
      cons: ['Longer recovery', 'Documentation required'],
      tone: 'info',
      recommended: d.holds?.hardship,
    },
  ];

  const handlePick = (opt) => {
    setPicked(opt.id);
    toast.success(`${opt.title} selected`, { description: opt.detail });
    onPick?.(opt);
  };

  return (
    <div className="surface-card p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-primary">Resolution options</p>
          <h3 className="text-base font-semibold mt-0.5">Compare strategies side-by-side</h3>
        </div>
        <Badge variant="outline" className="text-[10px] gap-1.5">
          <Bot className="h-3 w-3 text-primary" /> Ranked by expected recovery
        </Badge>
      </div>

      <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-3">
        {options.map((o) => (
          <div
            key={o.id}
            className={cn(
              'relative rounded-xl border bg-card p-3.5 flex flex-col gap-2 hover:shadow-md transition-all cursor-pointer',
              picked === o.id ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary/40',
              o.recommended && 'border-primary/60 bg-primary/5',
            )}
            onClick={() => handlePick(o)}
          >
            {o.recommended && (
              <span className="absolute -top-2 left-3 text-[9px] uppercase tracking-widest bg-primary text-primary-foreground px-1.5 py-0.5 rounded">
                Recommended
              </span>
            )}
            <div className="flex items-start justify-between">
              <div className={cn(
                'h-8 w-8 rounded-md flex items-center justify-center',
                o.tone === 'success' && 'bg-success/15 text-success',
                o.tone === 'primary' && 'bg-primary/15 text-gold-deep',
                o.tone === 'warning' && 'bg-warning/15 text-warning',
                o.tone === 'info' && 'bg-info/15 text-info',
              )}>
                <o.icon className="h-4 w-4" />
              </div>
              {picked === o.id && <CheckCircle2 className="h-4 w-4 text-primary" />}
            </div>
            <div>
              <p className="text-[13px] font-semibold leading-tight">{o.title}</p>
              <p className="font-mono-num text-[15px] text-foreground mt-0.5">{o.headline}</p>
            </div>
            <p className="text-[11px] text-muted-foreground leading-snug min-h-[28px]">{o.detail}</p>

            <div className="grid grid-cols-2 gap-1 text-[10px] text-muted-foreground border-t border-border pt-2">
              <div>
                <p className="uppercase tracking-wider">Recovery</p>
                <p className="font-mono-num text-foreground text-[12px]">{o.recovery}%</p>
              </div>
              <div>
                <p className="uppercase tracking-wider">Time</p>
                <p className="text-foreground text-[12px]">{o.time}</p>
              </div>
            </div>

            {o.gateReason && (
              <p className="text-[10px] text-warning leading-snug border-t border-warning/20 pt-1.5">{o.gateReason}</p>
            )}

            <Button
              size="sm"
              variant={picked === o.id ? 'default' : 'outline'}
              className={cn('mt-auto h-7 text-[11px]', picked === o.id && 'bg-primary text-primary-foreground hover:bg-primary/90')}
            >
              {picked === o.id ? 'Selected' : 'Pick'} <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ResolutionOptions;
